import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { UiLink } from '../../ui';
import { cn } from '../../utils';
import type { MarkdownTypes as Types } from './Markdown.types';

function getCommonComponents(propsOverride?: Types.ComponentPropsOverride) {
	return {
		p: ({ children, node, className, ...rest }: Types.CommonComponentProps<'p'>) => {
			const { className: overrideClassName, ...overrideProps } = propsOverride?.p ?? {};
			return (
				<p className={cn('leading-relaxed', className, overrideClassName)} {...rest} {...overrideProps}>
					{children}
				</p>
			);
		},
		h1: ({ children, node, className, ...rest }: Types.CommonComponentProps<'h1'>) => {
			const { className: overrideClassName, ...overrideProps } = propsOverride?.h1 ?? {};
			return (
				<h1 className={cn('font-semibold', className, overrideClassName)} {...rest} {...overrideProps}>
					{children}
				</h1>
			);
		},
		h2: ({ children, node, className, ...rest }: Types.CommonComponentProps<'h2'>) => {
			const { className: overrideClassName, ...overrideProps } = propsOverride?.h2 ?? {};
			return (
				<h2 className={cn('font-semibold', className, overrideClassName)} {...rest} {...overrideProps}>
					{children}
				</h2>
			);
		},
		h3: ({ children, node, className, ...rest }: Types.CommonComponentProps<'h3'>) => {
			const { className: overrideClassName, ...overrideProps } = propsOverride?.h3 ?? {};
			return (
				<h3 className={cn('font-semibold', className, overrideClassName)} {...rest} {...overrideProps}>
					{children}
				</h3>
			);
		},
		ul: ({ children, node, className, ...rest }: Types.CommonComponentProps<'ul'>) => {
			const { className: overrideClassName, ...overrideProps } = propsOverride?.ul ?? {};
			return (
				<ul className={cn('flex my-2 pl-4 gap-0.5 list-disc flex-col', className, overrideClassName)} {...rest} {...overrideProps}>
					{children}
				</ul>
			);
		},
		ol: ({ children, node, className, ...rest }: Types.CommonComponentProps<'ol'>) => {
			const { className: overrideClassName, ...overrideProps } = propsOverride?.ol ?? {};
			return (
				<ol className={cn('flex my-2 pl-4 gap-0.5 list-decimal flex-col', className, overrideClassName)} {...rest} {...overrideProps}>
					{children}
				</ol>
			);
		},
		li: ({ children, node, className, ...rest }: Types.CommonComponentProps<'li'>) => {
			const { className: overrideClassName, ...overrideProps } = propsOverride?.li ?? {};
			return (
				<li className={cn('leading-relaxed', className, overrideClassName)} {...rest} {...overrideProps}>
					{children}
				</li>
			);
		},
		strong: ({ children, node, className, ...rest }: Types.CommonComponentProps<'strong'>) => {
			const { className: overrideClassName, ...overrideProps } = propsOverride?.strong ?? {};
			return (
				<strong className={cn('font-semibold', className, overrideClassName)} {...rest} {...overrideProps}>
					{children}
				</strong>
			);
		},
		blockquote: ({ children, node, className, ...rest }: Types.CommonComponentProps<'div'>) => {
			const { className: overrideClassName, ...overrideProps } = propsOverride?.blockquote ?? {};
			return (
				<div className={cn('my-3 border-l-2 border-zinc-200 bg-zinc-50 p-2 pl-2', className, overrideClassName)} {...rest} {...overrideProps}>
					{children}
				</div>
			);
		},
		hr: ({ node, className, ...rest }: Types.CommonComponentProps<'hr'>) => {
			const { className: overrideClassName, ...overrideProps } = propsOverride?.hr ?? {};
			return <div className={cn('my-2 border-t border-zinc-100', className, overrideClassName)} {...rest} {...overrideProps} />;
		},
		a: ({ children, href, node, className, ...rest }: Types.CommonComponentProps<'a'>) => {
			const { className: overrideClassName, ...overrideProps } = propsOverride?.a ?? {};
			return (
				<UiLink className={cn('inline-block font-medium', className, overrideClassName)} href={href} target="_blank" {...rest} {...overrideProps}>
					{children}
				</UiLink>
			);
		},
	} as const;
}

/**
 * Markdown component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const Markdown: React.FunctionComponent<Types.Props> = (props) => {
	const { className: markdownClassName, components, componentProps, ...rest } = props;
	const commonComponents = React.useMemo(() => getCommonComponents(componentProps), [componentProps]);
	return (
		<ReactMarkdown
			className={cn('', markdownClassName)}
			remarkPlugins={[remarkBreaks, remarkGfm]}
			rehypePlugins={[rehypeRaw]}
			components={{ ...(commonComponents as typeof components), ...components }}
			{...rest}
		/>
	);
};

Markdown.displayName = 'Markdown';

/** Testing markdown cheatsheet: */
/*
```md
# Task list
- [x] Task 1
- [ ] Task 2
- [ ] Task 3

# Headers
# H1
## H2
### H3
#### H4
##### H5
###### H6

Alternatively, for H1 and H2, an underline-ish style:

Alt-H1
======

Alt-H2
------

# Emphasis
Emphasis, aka italics, with *asterisks* or _underscores_.

Strong emphasis, aka bold, with **asterisks** or __underscores__.

Combined emphasis with **asterisks and _underscores_**.

Strikethrough uses two tildes. ~~Scratch this.~~

# Lists

1. First ordered list item
2. Another item
  * Unordered sub-list.
1. Actual numbers don't matter, just that it's a number
  1. Ordered sub-list
4. And another item.
   
   Some text that should be aligned with the above item.

* Unordered list can use asterisks
- Or minuses
+ Or pluses

# Links
[I'm an inline-style link](https://www.google.com)

[I'm a reference-style link][Arbitrary case-insensitive reference text]

[You can use numbers for reference-style link definitions][1]

Or leave it empty and use the [link text itself]

URLs and URLs in angle brackets will automatically get turned into links.
http://www.example.com or <http://www.example.com> and sometimes
example.com (but not on Github, for example).

Some text to show that the reference links can follow later.

[arbitrary case-insensitive reference text]: https://www.mozilla.org
[1]: http://slashdot.org
[link text itself]: http://www.reddit.com

# Images
Here's our logo (hover to see the title text):

Inline-style:
![alt text](https://github.com/adam-p/markdown-here/raw/master/src/common/images/icon48.png "Logo Title Text 1")

Reference-style:
![alt text][logo]

[logo]: https://github.com/adam-p/markdown-here/raw/master/src/common/images/icon48.png "Logo Title Text 2"

# Code and Syntax Highlighting
Inline `code` has `back-ticks around` it.

```javascript
var s = "JavaScript syntax highlighting";
alert(s);
```

```python
s = "Python syntax highlighting"
print s
```

```
No language indicated, so no syntax highlighting.
But let's throw in a <b>tag</b>.
```

# Tables
Colons can be used to align columns.

| Tables        | Are           | Cool  |
| ------------- |:-------------:| -----:|
| col 3 is      | right-aligned | $1600 |
| col 2 is      | centered      |   $12 |
| zebra stripes | are neat      |    $1 |

The outer pipes (|) are optional, and you don't need to make the raw Markdown line up prettily. You can also use inline Markdown.

Markdown | Less | Pretty
--- | --- | ---
*Still* | `renders` | **nicely**
1 | 2 | 3

# Blockquotes

> Blockquotes are very handy in email to emulate reply text.
> This line is part of the same quote.

Quote break.

> This is a very long line that will still be quoted properly when it wraps. Oh boy let's keep writing to make sure this is long enough to actually wrap for everyone. Oh, you can *put* **Markdown** into a blockquote.

# Inline HTML
<dl>
  <dt>Definition list</dt>
  <dd>Is something people use sometimes.</dd>

  <dt>Markdown in HTML</dt>
  <dd>Does *not* work **very** well. Use HTML <em>tags</em>.</dd>
</dl>

# Horizontal Rule

Three or more...

---

Hyphens

***

Asterisks

___

Underscores

# YouTube Videos
<a href="http://www.youtube.com/watch?feature=player_embedded&v=YOUTUBE_VIDEO_ID_HERE
" target="_blank"><img src="http://img.youtube.com/vi/YOUTUBE_VIDEO_ID_HERE/0.jpg"
alt="IMAGE ALT TEXT HERE" width="240" height="180" border="10" /></a>

[![IMAGE ALT TEXT HERE](http://img.youtube.com/vi/YOUTUBE_VIDEO_ID_HERE/0.jpg)](http://www.youtube.com/watch?v=YOUTUBE_VIDEO_ID_HERE)

# TeX Mathematical Formulae
$-b \pm \sqrt{b^2 - 4ac} \over 2a$
$x = a_0 + \frac{1}{a_1 + \frac{1}{a_2 + \frac{1}{a_3 + a_4}}}$
$\forall x \in X, \quad \exists y \leq \epsilon$
```
*/
